import type { ContactPoint, Identifier } from "@medplum/fhirtypes";

/**
 * Extracts the ID from a FHIR reference string.
 *
 * @example
 * getReferenceId('Patient/123')           // '123'
 * getReferenceId('Patient/123', 'Patient') // '123'
 * getReferenceId('Patient/123', 'Practitioner') // undefined
 * getReferenceId(undefined)               // undefined
 */
export const getReferenceId = (
  ref: string | undefined,
  resourceType?: string
): string | undefined => {
  if (resourceType !== undefined && !ref?.startsWith(`${resourceType}/`)) {
    return undefined;
  }

  return ref?.split("/")[1];
};

/**
 * Returns the value of the first telecom entry matching the given system.
 *
 * @example
 * getTelecomValue(patient.telecom, 'email') // 'jane@example.com'
 * getTelecomValue(undefined, 'phone')       // undefined
 */
export const getTelecomValue = (
  telecom: ContactPoint[] | undefined,
  system: ContactPoint["system"]
): string | undefined =>
  telecom?.find((contactPoint) => contactPoint.system === system)?.value;

/**
 * Every identifier value of `system` across the records, e.g. every
 * location ID AKB gave a clinic's locations. Another system's identifier on
 * the same record is not counted.
 */
export const getIdentifierValues = (
  records: { identifier?: Identifier[] }[],
  system: string
): string[] =>
  records
    .flatMap(({ identifier = [] }) => identifier)
    .filter((identifier) => identifier.system === system)
    .map(({ value }) => value)
    .filter((value): value is string => value !== undefined);
